import { useSuspenseQuery } from '@tanstack/react-query';
import useClearHistory, { useClearHistoryStats, ClearHistoryParams } from '@/enterprise/data/useClearHistory';
import { fetchRecentClearsExistenceProbe } from '@/enterprise/data/useRecentClearsProbe';
import { MarketHistoryUrlState } from '@/enterprise/pages/MarketHistory';

const useMarketHistoryData = ({
  params,
  takerUuid,
  marketUuid,
  makerOrganizationUuid,
  currency,
}: {
  params: MarketHistoryUrlState;
  takerUuid: string;
  marketUuid: string;
  makerOrganizationUuid: string;
  currency: string;
}) => {
  const endDateForHistory = params.includeFuturePayDates ? undefined : params.endDate;

  // The dates sent in the request are processed as deposit dates
  const startDateParam = 'startDepositDate';
  const endDateParam = 'endDepositDate';

  const sort = params.sort as ClearHistoryParams['sort'];

  // Table data is called without a toCurrency parameter so the clear amounts are returned in the original currency
  const { data: tableData = [], isFetching: isFetchingTableData } = useClearHistory(
    takerUuid,
    marketUuid,
    makerOrganizationUuid,
    {
      [startDateParam]: params.startDate,
      [endDateParam]: endDateForHistory,
      limit: params.limit,
      sort,
      sortOrder: params.sortDirection,
      page: params.page,
    }
  );

  const hasClearHistory = !!tableData?.length;

  const { data: allTimeData } = useSuspenseQuery({
    queryKey: ['recent-clears-existence-probe', 'market', takerUuid, marketUuid, makerOrganizationUuid],
    queryFn: () =>
      !hasClearHistory
        ? fetchRecentClearsExistenceProbe({
            supplierMarket: { marketUuid, supplierDivisionUuid: takerUuid, makerOrganizationUuid },
          })
        : [],
  });

  const hasHistoryOutsideRange = !hasClearHistory && (allTimeData?.length ?? 0) > 0;

  const { data: chartData, isFetching: isFetchingChartData } = useClearHistory(
    takerUuid,
    marketUuid,
    makerOrganizationUuid,
    {
      toCurrency: currency,
      [startDateParam]: params.startDate,
      [endDateParam]: endDateForHistory,
      limit: 10000, // increase limit for chart data
      sort: 'depositDate',
      sortOrder: 'asc',
      isPendingClear: 'false',
    }
  );

  const { data: stats, isFetching: isFetchingStats } = useClearHistoryStats(
    takerUuid,
    marketUuid,
    makerOrganizationUuid,
    {
      toCurrency: currency,
      [startDateParam]: params.startDate,
      [endDateParam]: endDateForHistory,
      isPendingClear: 'false',
    }
  );

  // In SEA, stats endpoint return the latest clear pay date instead of making another API call
  const latestPayDate = stats?.latestClearPayDate;
  const latestDepositDate = stats?.latestClearDepositDate;
  const minimumPayDate = stats?.minimumClearPayDate;
  const minimumDepositDate = stats?.minimumClearDepositDate;

  return {
    data: {
      chartData,
      hasClearHistory,
      hasHistoryOutsideRange,
      minimumPayDate,
      minimumDepositDate,
      latestPayDate,
      latestDepositDate,
      stats,
      tableData: tableData ?? [],
    },
    isFetchingChartData: isFetchingChartData,
    isFetchingLatestPayDate: isFetchingStats,
    isFetchingLatestDepositDate: isFetchingStats,
    isFetchingMinimumPayDate: isFetchingStats,
    isFetchingMinimumDepositDate: isFetchingStats,
    isFetchingStats: isFetchingStats,
    isFetchingTableData: isFetchingTableData,
  };
};

export default useMarketHistoryData;
